import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './rush.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { DashboardHero, EditorMock, TMList, Integrations } from './RushDashMocks';
import WaitlistForm from './WaitlistForm';
import RushTryDemo from './RushTryDemo';
import LocalizedMeta from '../../i18n/LocalizedMeta';
import useLocalePath from '../../i18n/useLocalePath';
import { usePricing, CurrencyToggle } from './usePricing';

const LogoCloud = () => {
  const { t } = useTranslation('landing');
  return (
    <div className="logos">
      <div className="rs-container">
        <div className="logos-label">{t('home.logos.label')}</div>
        <div className="logos-row">
          <span className="logo-mark" style={{ fontFamily: 'Georgia, serif', fontSize: 22 }}>Northwind</span>
          <span className="logo-mark" style={{ letterSpacing: '0.2em', fontWeight: 600 }}>HELIOS</span>
          <span className="logo-mark" style={{ fontStyle: 'italic', fontWeight: 600 }}>tideline</span>
          <span className="logo-mark"><svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="2" /></svg>Orbital</span>
          <span className="logo-mark" style={{ fontFamily: 'Inter', fontWeight: 800, letterSpacing: '-0.03em' }}>Klein/Co</span>
          <span className="logo-mark"><svg width="18" height="18" viewBox="0 0 18 18"><rect x="3" y="3" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" transform="rotate(45 9 9)" /></svg>Quanta</span>
          <span className="logo-mark" style={{ fontWeight: 700 }}>Maravel</span>
        </div>
      </div>
    </div>
  );
};

const Bento = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="section">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">{t('home.bento.eyebrow')}</div>
          <h2>{t('home.bento.h2Lead')}<br />{t('home.bento.h2Sub')}</h2>
          <p className="section-lead">{t('home.bento.lead')}</p>
        </div>
        <div className="bento stagger">
          <div className="bento-card span-4 tall">
            <div className="feature-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="3" rx="1" fill="currentColor" opacity="0.35" /><rect x="2" y="8" width="10" height="3" rx="1" fill="currentColor" opacity="0.6" /><rect x="2" y="13" width="6" height="2" rx="1" fill="currentColor" /></svg>
            </div>
            <h3>{t('home.bento.cards.overview.title')}</h3>
            <p>{t('home.bento.cards.overview.desc')}</p>
            <div className="bento-visual">
              <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-soft)' }}>
                <div className="proj-row head" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr' }}>
                  <span>Project</span><span>Lang</span><span>Progress</span><span>Status</span>
                </div>
                <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                  <span className="proj-name"><span className="file-ic">DOC</span><span>Q4 Update</span></span>
                  <span><span className="lang-chip">DE</span> <span className="lang-chip">FR</span></span>
                  <span className="proj-progress"><span className="bar"><span style={{ width: '68%' }}></span></span><span className="proj-pct">68%</span></span>
                  <span><span className="status-pill live"><span className="pulse"></span>live</span></span>
                </div>
                <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                  <span className="proj-name"><span className="file-ic">IDM</span><span>Brand v3</span></span>
                  <span><span className="lang-chip">ES</span> <span className="lang-chip">IT</span></span>
                  <span className="proj-progress"><span className="bar"><span style={{ width: '92%' }}></span></span><span className="proj-pct">92%</span></span>
                  <span><span className="status-pill live"><span className="pulse"></span>live</span></span>
                </div>
                <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                  <span className="proj-name"><span className="file-ic">JSO</span><span>Onboarding</span></span>
                  <span><span className="lang-chip">+6</span></span>
                  <span className="proj-progress"><span className="bar"><span style={{ width: '40%' }}></span></span><span className="proj-pct">40%</span></span>
                  <span><span className="status-pill queued"><span className="pulse"></span>queued</span></span>
                </div>
              </div>
            </div>
          </div>

          <div className="bento-card span-2 tall">
            <div className="feature-icon indigo">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 5h12M3 9h12M3 13h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </div>
            <h3>{t('home.bento.cards.editor.title')}</h3>
            <p>{t('home.bento.cards.editor.desc')}</p>
            <div className="bento-visual">
              <div style={{ background: 'var(--bg-soft)', borderRadius: 8, padding: 10, fontSize: 12 }}>
                <div style={{ color: 'var(--ink-4)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>EN</div>
                <div style={{ color: 'var(--ink-2)', marginBottom: 8 }}>Welcome to your workspace.</div>
                <div style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>DE · AI</div>
                <div style={{ color: 'var(--ink)', fontWeight: 500 }}>Willkommen in Ihrem Workspace.</div>
              </div>
            </div>
          </div>

          <div className="bento-card span-2">
            <div className="feature-icon green">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" fill="none" /><path d="M9 5v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </div>
            <h3>{t('home.bento.cards.memory.title')}</h3>
            <p>{t('home.bento.cards.memory.desc')}</p>
          </div>

          <div className="bento-card span-2">
            <div className="feature-icon yellow">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M5 11a3 3 0 010-6 4 4 0 017.5-1A3.5 3.5 0 1114 13H5z" stroke="currentColor" strokeWidth="1.7" fill="none" /></svg>
            </div>
            <h3>{t('home.bento.cards.files.title')}</h3>
            <p>{t('home.bento.cards.files.desc')}</p>
            <div className="bento-visual">
              <Integrations />
            </div>
          </div>

          <div className="bento-card span-2">
            <div className="feature-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="4" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" fill="none" /><path d="M3 8h12M7 2v3M11 2v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </div>
            <h3>{t('home.bento.cards.deadlines.title')}</h3>
            <p>{t('home.bento.cards.deadlines.desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureRows = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
      <div className="rs-container">
        <div className="feature-row reveal">
          <div className="feature-text">
            <div className="eyebrow">{t('home.featureRows.editor.eyebrow')}</div>
            <h2 style={{ fontSize: 36 }}>{t('home.featureRows.editor.h2')}</h2>
            <p className="section-lead">{t('home.featureRows.editor.lead')}</p>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.editor.b1term')}</b> · {t('home.featureRows.editor.b1desc')}</span></div>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.editor.b2term')}</b> · {t('home.featureRows.editor.b2desc')}</span></div>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.editor.b3term')}</b> · {t('home.featureRows.editor.b3desc')}</span></div>
            </div>
          </div>
          <div className="product-window" style={{ borderRadius: 16 }}>
            <div className="product-chrome">
              <div className="traffic"><span /><span /><span /></div>
              <div className="url">rush.studio/projects/q4/segment/12</div>
            </div>
            <div style={{ padding: 16, background: 'var(--bg-card)' }}><EditorMock /></div>
          </div>
        </div>

        <div className="feature-row reverse reveal">
          <div className="product-window">
            <div className="product-chrome">
              <div className="traffic"><span /><span /><span /></div>
              <div className="url">rush.studio/memory</div>
            </div>
            <div style={{ padding: 20, background: 'var(--bg-card)' }}>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12, fontWeight: 600 }}>{t('home.featureRows.memory.matchLabel')}</div>
              <TMList />
              <div style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-4)' }}>{t('home.featureRows.memory.moreMatches')}</div>
            </div>
          </div>
          <div className="feature-text">
            <div className="eyebrow">{t('home.featureRows.memory.eyebrow')}</div>
            <h2 style={{ fontSize: 36 }}>{t('home.featureRows.memory.h2Lead')}<br />{t('home.featureRows.memory.h2Sub')}</h2>
            <p className="section-lead">{t('home.featureRows.memory.lead')}</p>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.memory.b1term')}</b> · {t('home.featureRows.memory.b1desc')}</span></div>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.memory.b2term')}</b> · {t('home.featureRows.memory.b2desc')}</span></div>
              <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>{t('home.featureRows.memory.b3term')}</b> · {t('home.featureRows.memory.b3desc')}</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const STEP_ICONS = {
  upload: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  assign: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 19c.8-3 3.4-4.5 6-4.5s5.2 1.5 6 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 6h5M18.5 3.5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  translate: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 5h8M7 3v2M5 5c0 4 2 7 6 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11 13c-2 0-4-1-5-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M13 21l4-10 4 10M14.5 17h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  export: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12V6a2 2 0 012-2h7l5 5v7a2 2 0 01-2 2h-3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 4v4a2 2 0 002 2h3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M3 16h9m0 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const DIFF_META = [
  { tint: '', to: '/features/brand-voice', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 7v4M6.5 4.5v9M10 6v6M13.5 4v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>) },
  { tint: 'indigo', to: '/features/automations', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="6" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.7" /><rect x="10" y="10.5" width="6" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.7" /><path d="M8 5.2h3a2 2 0 012 2v3.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>) },
  { tint: 'green', to: '/features/ai-quality', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 13a6 6 0 1112 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M9 13l3.5-3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>) },
  { tint: 'yellow', to: '/features/preview', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 9s2.8-5 7-5 7 5 7 5-2.8 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.7" /></svg>) },
  { tint: '', to: '/features/layout-risk', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2.5l6.5 12h-13L9 2.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M9 7.5v3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="9" cy="13" r="0.6" fill="currentColor" /></svg>) },
];

const Differentiators = () => {
  const { t } = useTranslation('landing');
  const lp = useLocalePath();
  const copy = t('home.differentiators.cards', { returnObjects: true });
  const cards = Array.isArray(copy) ? DIFF_META.map((m, i) => ({ ...m, ...(copy[i] || {}) })) : [];
  return (
    <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">{t('home.differentiators.eyebrow')}</div>
          <h2>{t('home.differentiators.h2')}</h2>
          <p className="section-lead">{t('home.differentiators.lead')}</p>
        </div>
        <div className="reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))', gap: 16, marginTop: 8 }}>
          {cards.map((c) => (
            <Link className="bento-card" key={c.to} to={lp(c.to)} style={{ display: 'flex', flexDirection: 'column', textDecoration: 'none', color: 'inherit' }}>
              <div className={`feature-icon ${c.tint}`}>{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <span style={{ marginTop: 'auto', paddingTop: 16, color: 'var(--accent)', fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.cta}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h9M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="section">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">{t('home.howItWorks.eyebrow')}</div>
          <h2>{t('home.howItWorks.h2')}</h2>
        </div>
        <div className="steps stagger">
          <div className="step">
            <div className="step-head">
              <div className="step-icon">{STEP_ICONS.upload}</div>
              <div className="step-num">1</div>
            </div>
            <h4>{t('home.howItWorks.steps.upload.title')}</h4>
            <p>{t('home.howItWorks.steps.upload.desc')}</p>
          </div>
          <div className="step">
            <div className="step-head">
              <div className="step-icon">{STEP_ICONS.assign}</div>
              <div className="step-num">2</div>
            </div>
            <h4>{t('home.howItWorks.steps.assign.title')}</h4>
            <p>{t('home.howItWorks.steps.assign.desc')}</p>
          </div>
          <div className="step">
            <div className="step-head">
              <div className="step-icon">{STEP_ICONS.translate}</div>
              <div className="step-num">3</div>
            </div>
            <h4>{t('home.howItWorks.steps.translate.title')}</h4>
            <p>{t('home.howItWorks.steps.translate.desc')}</p>
          </div>
          <div className="step">
            <div className="step-head">
              <div className="step-icon">{STEP_ICONS.export}</div>
              <div className="step-num">4</div>
            </div>
            <h4>{t('home.howItWorks.steps.export.title')}</h4>
            <p>{t('home.howItWorks.steps.export.desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stats = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="section-tight">
      <div className="rs-container">
        <div className="stats reveal stagger">
          <div className="stat"><div className="stat-num">100<span className="accent">+</span></div><div className="stat-label">{t('home.stats.languages')}</div></div>
          <div className="stat"><div className="stat-num">3.2<span className="accent">×</span></div><div className="stat-label">{t('home.stats.turnaround')}</div></div>
          <div className="stat"><div className="stat-num">62<span className="accent">%</span></div><div className="stat-label">{t('home.stats.rework')}</div></div>
          <div className="stat"><div className="stat-num">SOC 2</div><div className="stat-label">{t('home.stats.compliance')}</div></div>
        </div>
      </div>
    </section>
  );
};

const PLAN_META = [
  { slug: 'free', monthly: 0, highlight: false },
  { slug: 'essential', monthly: 79, highlight: false },
  { slug: 'growth', monthly: 249, highlight: true },
  { slug: 'scale', monthly: 599, highlight: false },
];

const planTarget = (plan, currency) =>
  `/register?plan=${plan.slug}${currency ? `&currency=${currency}` : ''}`;

const Pricing = ({ billing, setBilling }) => {
  const { t } = useTranslation('landing');
  const lp = useLocalePath();
  const { currencies, currency, symbol, setCurrency, planByKey } = usePricing();
  const yearly = billing === 'yearly';
  const copy = t('home.pricing.plans', { returnObjects: true });
  const plans = Array.isArray(copy) ? PLAN_META.map((m, i) => ({ ...m, ...(copy[i] || {}) })) : [];
  return (
    <section className="section" id="pricing">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">{t('home.pricing.eyebrow')}</div>
          <h2>{t('home.pricing.h2')}</h2>
          <p className="section-lead">{t('home.pricing.lead')}</p>
        </div>

        <div className="pricing-controls">
          <div className="billing-toggle" role="tablist" aria-label={t('home.pricing.billingLabel')}>
            <button role="tab" aria-selected={!yearly} className={`billing-btn ${!yearly ? 'is-active' : ''}`} onClick={() => setBilling('monthly')}>{t('home.pricing.monthly')}</button>
            <button role="tab" aria-selected={yearly} className={`billing-btn ${yearly ? 'is-active' : ''}`} onClick={() => setBilling('yearly')}>
              {t('home.pricing.yearly')} <span className="billing-save">{t('home.pricing.yearlySave')}</span>
            </button>
          </div>
          <CurrencyToggle currencies={currencies} currency={currency} setCurrency={setCurrency} />
        </div>

        <div className="pricing stagger">
          {plans.map((plan) => {
            const isFree = plan.monthly === 0;
            // Live per-currency price from /api/plans; fall back to the hardcoded
            // GBP amount until the API responds (or if it fails).
            const priceObj = planByKey(plan.slug)?.prices?.[currency];
            const monthlyAmt = priceObj ? priceObj.monthly : plan.monthly;
            const yearlyAmt = priceObj ? priceObj.yearly : plan.monthly * 10;
            const price = isFree ? 0 : (yearly ? yearlyAmt : monthlyAmt);
            const period = isFree ? t('home.pricing.forever') : (yearly ? t('home.pricing.perYear') : t('home.pricing.perMonth'));
            const features = Array.isArray(plan.features) ? plan.features : [];
            return (
              <div key={plan.slug} className={`price-card ${plan.highlight ? 'featured' : ''}`}>
                {plan.highlight && <span className="price-tag">{t('home.pricing.mostPopular')}</span>}
                <div className="price-name">{plan.name}</div>
                <div className="price-amount">
                  <span className="price-currency">{symbol}</span>
                  <span className="num">{price.toLocaleString()}</span>
                  <span className="per">{period}</span>
                </div>
                <div className="price-sub">{plan.description}</div>
                <ul className="price-features">
                  {features.map((f) => (
                    <li key={f} className="price-feature">
                      <svg className="price-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={lp(planTarget(plan, currency))} className={`btn ${plan.highlight ? 'btn-accent' : 'btn-ghost'}`} style={{ marginTop: 'auto' }}>{plan.cta}</Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="section">
      <div className="rs-container">
        <div className="final-cta reveal reveal-scale">
          <h2>{t('home.finalCta.h2')}</h2>
          <p>{t('home.finalCta.lead')}</p>
          <WaitlistForm source="landing-final" />
        </div>
      </div>
    </section>
  );
};

const RushLandingPage = () => {
  const { t, i18n } = useTranslation('landing');
  const [billing, setBilling] = useState('monthly');
  useRushReveal();

  return (
    <div className="rs">
      <LocalizedMeta path="/" lang={i18n.language} title={t('home.meta.title')} description={t('home.meta.description')} />
      <RushNav />
      <section className="hero">
        <div className="hero-mesh"></div>
        <div className="rs-container hero-inner hero-anim">
          <span className="hero-eyebrow"><span className="dot"></span>{t('home.hero.eyebrow')}</span>
          <h1>{t('home.hero.titleLead')}<em>{t('home.hero.titleEm')}</em></h1>
          <p className="hero-sub">
            {t('home.hero.sub')}
          </p>
          <WaitlistForm source="landing-hero" inputWidth={280} className="hero-cta-row" />
          <div className="hero-meta">
            <span className="hero-meta-item">{t('home.hero.meta.languages')}</span>
            <span className="hero-meta-item">{t('home.hero.meta.security')}</span>
            <span className="hero-meta-item">{t('home.hero.meta.integrations')}</span>
            <span className="hero-meta-item">{t('home.hero.meta.noCard')}</span>
          </div>

          <div className="hero-product reveal reveal-scale">
            <div className="product-window">
              <div className="product-chrome">
                <div className="traffic"><span /><span /><span /></div>
                <div className="url">rush.studio/dashboard</div>
              </div>
              <DashboardHero />
            </div>
          </div>
        </div>
      </section>

      <LogoCloud />
      <Bento />
      <RushTryDemo />
      <FeatureRows />
      <Differentiators />
      <HowItWorks />
      <Stats />
      <Pricing billing={billing} setBilling={setBilling} />
      <FinalCTA />
      <RushFooter />
    </div>
  );
};

export default RushLandingPage;
